import * as React from 'react';
import { connect } from 'react-redux';

import InputCheckbox from 'components/inputs/input-checkbox/InputCheckbox';
import ImageUploader, { MEDIA_TYPES_IMAGES } from 'components/ImageUploader';
import AddNew from 'components/buttons/add-new/AddNew';
import ActionLink from 'components/buttons/action-link/ActionLink';

import SharedOutfitArticleInput from '../article-input/SharedOutfitArticleInput';
import SharedOutfitArticleImageGrid from '../image-grid/SharedOutfitArticleImageGrid';
import SharedOutfitUploadPlaceholder from '../upload-placeholder/SharedOutfitUploadPlaceholder';

import { addOutfitItem, deleteOutfit, updateArticleInfo, updateOutfitInfo, uploadOutfitImage } from '../../reducers/sharedAccessOutfits';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { __t, applyTranslations } from 'utils/language';

import type { SharedAccessOutfit } from '../../types/shared-access-outfits.type';

import './shared-outfit-section.scss';

applyTranslations(require('./shared-outfit-section.i18n.json'));

type OwnProps = {
  index: number,
  outfit: SharedAccessOutfit
};

type DispatchProps = {
  dispatchUploadOutfitImage: (a: File) => Promise<void>,
  dispatchUpdateOutfit: (a: SharedAccessOutfit) => Promise<void>,
  dispatchUpdateArticleInfo: (b: number, a: string) => Promise<void>,
  dispatchDeleteOutfit: (b?: number | null, a?: number | null) => Promise<void>,
  dispatchAddItem: (a: number) => void,
  dispatchShowConfirmationModal: (
    params: {
      title: string,
      message: string,
      confirmButtonLabel: string,
      innerContent: React.ReactNode,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & DispatchProps;

class SharedOutfitSection extends React.PureComponent<Props> {
  handleOutfitTypeUpdate = () => {
    const { outfit, dispatchUpdateOutfit } = this.props;

    if (outfit?.postType === 'instagram') {
      dispatchUpdateOutfit({
        ...outfit,
        postType: 'other'
      });
    } else {
      dispatchUpdateOutfit({
        ...outfit,
        postType: 'instagram'
      });
    }
  };

  handleArticleSizeChange = (articleIndex: number, newValue: string): void => {
    const { outfit, dispatchUpdateOutfit } = this.props;

    dispatchUpdateOutfit({
      ...outfit,
      items: outfit?.items.map((item, index) => {
        if (index === articleIndex) {
          return {
            ...item,
            size: newValue
          };
        }

        return item;
      }) || []
    });
  };

  handleArticleURLChange = (articleIndex: number, newValue: string): void => {
    const { outfit, dispatchUpdateOutfit, dispatchUpdateArticleInfo } = this.props;

    if (!newValue) {
      // drop current line
      dispatchUpdateOutfit({
        ...outfit,
        items: outfit?.items.map((item, index) => {
          if (index === articleIndex) {
            return {};
          }

          return item;
        }) || []
      });
    } else {
      // fetch article info and display it
      dispatchUpdateArticleInfo(articleIndex, newValue);
    }
  };

  handleDeleteOutfit = () => {
    const { dispatchDeleteOutfit, dispatchShowConfirmationModal, dispatchHideConfirmationModal, outfit, index } = this.props;
    dispatchShowConfirmationModal({
      title: __t('shared_outfit_section.outfit_delete_confirmation.title'),
      message: __t('shared_outfit_section.outfit_delete_confirmation.message'),
      confirmButtonLabel: __t('shared_outfit_section.outfit_delete_confirmation.button_confirm'),
      innerContent: this.renderContent(),
      onConfirm: () =>
        dispatchDeleteOutfit(outfit.id, index)
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () => dispatchHideConfirmationModal()
    });
  };

  addNewItem = (): void => {
    const { dispatchAddItem, index } = this.props;
    dispatchAddItem(index);
  };

  renderArticles(): React.ReactNode {
    const { index, outfit } = this.props;

    return outfit.items
      .map((_, articleIndex) =>
        <SharedOutfitArticleInput
          key={`outfit-article-${index}-${articleIndex}`}
          index={articleIndex}
          onUrlChange={this.handleArticleURLChange}
          onSizeChange={this.handleArticleSizeChange}
          article={outfit?.items?.[articleIndex]}
        />
      );
  }

  renderContent(): React.ReactNode {
    return (
      <div className="shared-outfit-section__modal-inner-content">
        {__t('shared_outfit_section.outfit_delete_confirmation.content_message')}
      </div>
    );
  }

  renderAddNewItemBtn = (iconName: string): JSX.Element => <AddNew
    text={__t('shared_outfit_section.add_new_item')}
    onClick={this.addNewItem}
    icon={{
      name: iconName,
      width: 16,
      height: 16
    }}
  />;

  render(): React.ReactNode {
    const { index, outfit, dispatchUploadOutfitImage } = this.props;
    const isDeletable = outfit.items && (outfit.imageUrl || !!outfit.items.filter((item) => item.itemUrl).length);
    const canAddNewItem = (outfit.items || []).length < 9;
    const ICON = 'plus-inside-circle-custom-bg-white-fg';

    return (
      <section className="shared-outfit-section">
        <div className="shared-outfit-section__info">
          <header className="shared-outfit-section__header">
            <span>{__t('shared_outfit_section.header_label', { index: index + 1 })}</span>
            {
              isDeletable &&
                <ActionLink
                  color="light"
                  size="normal"
                  onClick={this.handleDeleteOutfit}
                >
                  {__t('shared_outfit_section.delete_outfit_label')}
                </ActionLink>
            }
          </header>
          <ImageUploader
            multiple={false}
            mediaTypes={MEDIA_TYPES_IMAGES}
            onUpload={dispatchUploadOutfitImage}
          >
            <SharedOutfitUploadPlaceholder imageUrl={outfit?.imageUrl} />
          </ImageUploader>
          <div className="shared-outfit-section__ig-checkbox">
            <InputCheckbox
              id={`outfit-type-${index}`}
              labelClass="shared-outfit-section__ig-checkbox-label"
              label={__t('shared_outfit_section.instagram_checkbox_label')}
              checked={outfit?.postType === 'instagram'}
              onChange={this.handleOutfitTypeUpdate}
            />
          </div>
        </div>
        <div className="shared-outfit-section__articles">
          {this.renderArticles()}
          {canAddNewItem && this.renderAddNewItemBtn(ICON)}
        </div>
        <div className="shared-outfit-section__images">
          <SharedOutfitArticleImageGrid
            articles={outfit?.items ?? []}
          />
        </div>
      </section>
    );
  }
}

function mapDispatchToProps(dispatch, ownProps: OwnProps): DispatchProps {
  return {
    dispatchUploadOutfitImage: (file: File) => dispatch(uploadOutfitImage(ownProps.index, file)),
    dispatchUpdateOutfit: (outfit: SharedAccessOutfit) => dispatch(updateOutfitInfo(ownProps.index, outfit)),
    dispatchDeleteOutfit: (outfitId?: number | null, index?: number | null) => dispatch(deleteOutfit(outfitId, index)),
    dispatchAddItem: (outfitIndex: number) => dispatch(addOutfitItem(outfitIndex)),
    dispatchUpdateArticleInfo: (articleIndex: number, url: string) =>
      dispatch(updateArticleInfo(ownProps.index, articleIndex, url)),
    dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
    dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
  };
}

export default connect(undefined, mapDispatchToProps)(SharedOutfitSection);
